"use strict";

(() => {
  // ============================================================================
  // CONSTANTS & CONFIGURATION
  // ============================================================================
  const STEP_ORDER = ['size', 'color', 'flap', 'summary'];
  const STEP_SELECTORS = {
    sizeColor: '[data-step="size-color"]',
    freeFlap: '[data-step="free-flap"]',
    flap: '[data-step="flap"]'
  };
  const ROW_TO_POPUP_STEP = {
    'size-color': 'size',
    'free-flap': 'color',
    flap: 'flap'
  };
  const DATA_ATTRS = {
    listenerAdded: 'data-listener-added',
    productId: 'data-product-id',
    variantId: 'data-variant-id',
    step: 'data-step',
    colorVariantId: 'data-color-variant-id',
    colorVariantVariantId: 'data-color-variant-variant-id',
    flapId: 'data-flap-id',
    flapVariantId: 'data-flap-variant-id',
    dynamicImage: 'data-dynamic-image'
  };

  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  let selectedProducts = {
    size: null,
    color: null,
    flap: null
  };

  let productsData = null;
  let isLoading = false;
  let addedToCart = false;

  // ============================================================================
  // ERROR MESSAGES (können über Shopify Locales überschrieben werden)
  // ============================================================================
  const ERROR_MESSAGES = {
    cartAdd: 'Es gab ein Problem beim Hinzufügen zum Warenkorb. Bitte versuche es erneut.',
    dataLoad: 'Die Produktdaten konnten nicht geladen werden. Bitte lade die Seite neu.',
    generic: 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.'
  };

  // ============================================================================
  // DOM CACHE (wird bei Bedarf aktualisiert)
  // ============================================================================
  const domCache = {
    // Haupt-Elemente
    popup: null,
    popupContent: null,
    mainButton: null,
    
    // Content Items (außerhalb des Popups)
    contentItems: null,
    allContentItems: null,
    
    // Popup Steps
    steps: new Map(),
    
    // Popup Header Kategorien
    categories: null,
    
    // Häufig verwendete Container
    containers: {
      productImage: null,
      colorVariants: null,
      freeFlap: null,
      flapsContainer: null,
      flapImageMain: null,
      flapImageFlap: null,
      summaryItems: null,
      summaryPriceCurrent: null,
      summaryPriceCompare: null
    },
    
    // Buttons
    buttons: {
      summaryAddToCart: null,
      nextButtons: new Map(),
      previousButtons: null,
      backButtons: null
    },
    
    // Speichert den vollständigen Ausgangszustand der Auswahlzeilen
    initialContentItems: new Map(),
    initialImages: new Map(),
    
    /**
     * Initialisiert den DOM-Cache
     */
    _init() {
      // Haupt-Elemente
      this.popup = document.querySelector('.bs-configurator__popup');
      this.popupContent = document.querySelector('.bs-configurator__popup-content');
      this.mediaTrigger = document.querySelector('.bs-configurator__image');
      this.mainButton = document.querySelector('.bs-configurator__button-wrapper .bs-configurator__button');
      this.addToCartButton = document.querySelector('.bs-configurator__button-wrapper .bs-configurator__add-to-cart-button');
      this.checkoutButton = document.querySelector('.bs-configurator__checkout-button');
      this.selectionLabel = document.querySelector('.bs-configurator__selection-label');
      this.selectionTotal = document.querySelector('.bs-configurator__selection-total');
      this.selectionSuccess = document.querySelector('.bs-configurator__selection-success');
      this.selectionCompare = document.querySelector('.bs-configurator__selection-total-compare');
      this.selectionCurrent = document.querySelector('.bs-configurator__selection-total-current');
      
      // Content Items
      this.contentItems = {
        sizeColor: document.querySelector(`.bs-configurator__content-item${STEP_SELECTORS.sizeColor}`),
        freeFlap: document.querySelector(`.bs-configurator__content-item${STEP_SELECTORS.freeFlap}`),
        flap: document.querySelector(`.bs-configurator__content-item${STEP_SELECTORS.flap}`)
      };
      this.allContentItems = document.querySelectorAll('.bs-configurator__content-item');
      
      // Popup Steps cachen
      STEP_ORDER.forEach((step) => {
        const stepElement = document.querySelector(`.bs-configurator__popup-content-inner[${DATA_ATTRS.step}="${step}"]`);
        if (stepElement) {
          this.steps.set(step, stepElement);
        }
      });
      
      // Kategorien
      this.categories = document.querySelectorAll('.bs-configurator__popup-content-header-category');
      
      // Container
      this.containers.productImage = document.querySelector('.bs-configurator__popup-content-inner-color-product-image');
      this.containers.colorVariants = document.querySelector('.bs-configurator__popup-content-inner-color-product-color-variants');
      this.containers.freeFlap = document.querySelector('.bs-configurator__popup-content-inner-color-free-flap');
      this.containers.flapsContainer = document.querySelector('.bs-configurator__popup-content-inner-flap-products');
      this.containers.flapImageMain = document.querySelector('.bs-configurator__popup-content-inner-flap-product-image-main');
      this.containers.flapImageFlap = document.querySelector('.bs-configurator__popup-content-inner-flap-product-image-flap');
      this.containers.summaryItems = document.querySelector('.bs-configurator__popup-content-inner-summary-items');
      this.containers.summaryPriceCurrent = this.popup.querySelector('.bs-configurator__popup-content-inner-summary-total-price--current');
      this.containers.summaryPriceCompare = this.popup.querySelector('.bs-configurator__popup-content-inner-summary-total-price--compare');
      
      // Buttons
      this.buttons.summaryAddToCart = document.querySelector('.bs-configurator__popup-content-inner[data-step="summary"] .bs-configurator__popup-content-inner-button--add-to-cart');
      this.buttons.previousButtons = document.querySelectorAll('.bs-configurator__popup-content-inner-button--previous');
      this.buttons.backButtons = document.querySelectorAll('.bs-configurator__popup-back-button');
      
      // Next-Buttons pro Step cachen
      STEP_ORDER.forEach((step) => {
        const nextBtn = document.querySelector(`.bs-configurator__popup-content-inner[data-step="${step}"] .bs-configurator__popup-content-inner-button--next`);
        if (nextBtn) {
          this.buttons.nextButtons.set(step, nextBtn);
        }
      });
      
      // Speichere den Ausgangszustand für einen vollständigen Reset
      this._saveInitialContentItems();
    },
    
    /**
     * Speichert Inhalt und Klassen der Auswahlzeilen für späteres Wiederherstellen
     */
    _saveInitialContentItems() {
      this.allContentItems?.forEach((item) => {
        this.initialContentItems.set(item, {
          className: item.className,
          innerHTML: item.innerHTML
        });

        const imageContainer = item.querySelector('.bs-configurator__content-item-image');
        const step = item.getAttribute(DATA_ATTRS.step);
        if (imageContainer && step) {
          this.initialImages.set(step, imageContainer.innerHTML);
        }
      });
    },
    
    /**
     * Gibt den gecachten Step zurück
     * @param {string} step - Der Step-Name
     * @returns {HTMLElement|null}
     */
    getStep(step) {
      return this.steps.get(step) || null;
    },
    
    /**
     * Gibt den gecachten Next-Button für einen Step zurück
     * @param {string} step - Der Step-Name
     * @returns {HTMLElement|null}
     */
    getNextButton(step) {
      return this.buttons.nextButtons.get(step) || null;
    }
  };

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================
  const utils = {
    /**
     * Escaped HTML-Sonderzeichen um XSS zu verhindern
     * @param {string} str - Der zu escapende String
     * @returns {string} Der escapte String
     */
    escapeHTML(str) {
      if (str == null) return '';
      const div = document.createElement('div');
      div.textContent = String(str);
      return div.innerHTML;
    },

    /**
     * Escaped einen Wert für die Verwendung in HTML-Attributen
     * @param {string} str - Der zu escapende String
     * @returns {string} Der escapte String
     */
    escapeAttr(str) {
      if (str == null) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    },

    /**
     * Prüft ob ein Wert als "available" gilt
     * @param {*} value - Der zu prüfende Wert
     * @returns {boolean}
     */
    isAvailable(value) {
      return value === true || value === 'true' || value === 1 || 
             (value !== false && value !== 'false' && value !== 0);
    },

    /**
     * Prüft ob free_flap existiert und gültig ist
     */
    isValidFreeFlap(freeFlap) {
      return freeFlap && freeFlap !== null && freeFlap !== 'null';
    },

    /**
     * Formatiert Preis (Cent zu Euro)
     */
    formatPrice(price, formatted) {
      if (formatted) return formatted;
      if (price && !isNaN(price)) {
        return `${(price / 100).toFixed(2)}€`;
      }
      return '';
    },

    flapLabel(flap, separator = ' - ') {
      if (!flap) return '';
      const name = (flap.produkt_titel || '').trim() || flap.configurator_product_name || flap.title || '';
      const color = (flap.produkt_titel_farbe || '').trim();
      return color ? `${name}${separator}${color}` : name;
    },

    priceMarkup(priceFormatted, compareFormatted) {
      if (compareFormatted) {
        return `<span class="bs-configurator__content-item-price-strikethrough">${this.escapeHTML(compareFormatted)}</span><span class="bs-configurator__content-item-price-current">${this.escapeHTML(priceFormatted)}</span>`;
      }
      return this.escapeHTML(priceFormatted);
    },

    /**
     * Erstellt ein Bild-Element als HTML-String (mit XSS-Schutz)
     * @param {Object} options - Bild-Optionen
     * @param {string} options.src - Bild-URL
     * @param {string} [options.srcset] - Srcset für responsive Bilder
     * @param {string} [options.alt] - Alt-Text
     * @param {string} [options.loading='lazy'] - Loading-Attribut
     * @param {boolean} [options.dynamic=false] - Ob das Bild dynamisch hinzugefügt wurde
     * @returns {string} HTML-String des Bild-Elements
     */
    createImageHTML({ src, srcset, alt, loading = 'lazy', dynamic = false }) {
      if (!src) return '';
      const escapedAlt = this.escapeAttr(alt || '');
      const escapedSrc = this.escapeAttr(src);
      const attrs = [`src="${escapedSrc}"`, `alt="${escapedAlt}"`, `loading="${loading}"`];
      if (srcset) attrs.push(`srcset="${this.escapeAttr(srcset)}"`);
      if (dynamic) attrs.push(`${DATA_ATTRS.dynamicImage}="true"`);
      return `<img ${attrs.join(' ')} />`;
    },

    /**
     * Findet Produkt in productsData anhand der ID
     * @param {number} productId - Die Produkt-ID
     * @returns {Object|null} Das gefundene Produkt oder null
     */
    findProduct(productId) {
      return productsData?.products?.find((p) => p.id === productId) || null;
    },

    /**
     * Findet eine Color Variant in einem Produkt
     * @param {Object} product - Das Produkt-Objekt
     * @param {number} colorVariantId - Die Color Variant ID
     * @returns {Object|null} Die gefundene Color Variant oder null
     */
    findColorVariant(product, colorVariantId) {
      return product?.color_variants?.find((cv) => cv.id === colorVariantId) || null;
    },

    /**
     * Findet eine Flap in einer Color Variant
     * @param {Object} colorVariant - Die Color Variant
     * @param {number} flapId - Die Flap ID
     * @returns {Object|null} Die gefundene Flap oder null
     */
    findFlap(colorVariant, flapId) {
      if (this.isValidFreeFlap(colorVariant.free_flap) && colorVariant.free_flap.id === flapId) {
        return colorVariant.free_flap;
      }
      return colorVariant?.flaps?.find((f) => f.id === flapId) || null;
    },

    /**
     * Parst numerische Datenattribute von einem Element
     * @param {HTMLElement} element - Das DOM-Element
     * @param {string} attr - Der Attributname
     * @returns {number} Der geparste Integer-Wert
     */
    parseDataAttr(element, attr) {
      return parseInt(element?.getAttribute(attr) || '0', 10);
    },

    /**
     * Holt aktuell selektiertes Produkt und Color Variant
     * @returns {{product: Object|null, colorVariant: Object|null}}
     */
    getSelectedProductAndVariant() {
      if (!selectedProducts.size || !selectedProducts.color || !productsData) {
        return { product: null, colorVariant: null };
      }
      const product = this.findProduct(selectedProducts.size.productId);
      if (!product?.color_variants?.length) {
        return { product: null, colorVariant: null };
      }
      const colorVariant = this.findColorVariant(product, selectedProducts.color.productId);
      return { product, colorVariant };
    },

    /**
     * Sammelt Variant IDs aus Content Items
     */
    collectVariantIds() {
      const items = [];
      const steps = ['size-color', 'free-flap', 'flap'];
      
      steps.forEach((step) => {
        const item = document.querySelector(`.bs-configurator__content-item[data-step="${step}"]`);
        const variantId = item?.getAttribute(DATA_ATTRS.variantId);
        if (variantId) {
          items.push({ id: parseInt(variantId, 10), quantity: 1 });
        }
      });
      
      return items;
    },

    /**
     * Event Listener nur einmal hinzufügen
     */
    addEventListenerOnce(element, event, handler) {
      if (!element || element.hasAttribute(DATA_ATTRS.listenerAdded)) return;
      element.setAttribute(DATA_ATTRS.listenerAdded, 'true');
      element.addEventListener(event, handler);
    },

    /**
     * Entfernt Hash ohne Reload
     */
    removeHash() {
      if (window.location.hash === '#set-creator') {
        if (window.history?.replaceState) {
          window.history.replaceState(null, null, window.location.pathname + window.location.search);
        } else {
          window.location.hash = '';
        }
      }
    },

    /**
     * Zeigt eine Benachrichtigung an
     * @param {string} message - Die anzuzeigende Nachricht
     * @param {string} [type='error'] - Der Typ der Benachrichtigung ('error', 'success', 'info')
     * @param {number} [duration=4000] - Anzeigedauer in Millisekunden
     */
    showNotification(message, type = 'error', duration = 4000) {
      // Entferne existierende Notification
      const existingNotification = document.querySelector('.bs-configurator__notification');
      if (existingNotification) {
        existingNotification.remove();
      }

      // Erstelle neue Notification
      const notification = document.createElement('div');
      notification.className = `bs-configurator__notification bs-configurator__notification--${type}`;
      notification.setAttribute('role', 'alert');
      notification.setAttribute('aria-live', 'polite');
      
      const iconMap = {
        error: '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" stroke="currentColor" stroke-width="1.5"/><path d="M10 6V10M10 14H10.01" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
        success: '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" stroke="currentColor" stroke-width="1.5"/><path d="M7 10L9 12L13 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
        info: '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" stroke="currentColor" stroke-width="1.5"/><path d="M10 14V10M10 6H10.01" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>'
      };

      notification.innerHTML = `
        <span class="bs-configurator__notification-icon">${iconMap[type] || iconMap.info}</span>
        <span class="bs-configurator__notification-message">${this.escapeHTML(message)}</span>
        <button class="bs-configurator__notification-close" aria-label="Schließen">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
      `;

      // Füge zum Popup oder Body hinzu
      const popup = document.querySelector('.bs-configurator__popup.active');
      const container = popup || document.body;
      container.appendChild(notification);

      // Animation
      requestAnimationFrame(() => {
        notification.classList.add('bs-configurator__notification--visible');
      });

      // Schließen-Button
      const closeBtn = notification.querySelector('.bs-configurator__notification-close');
      closeBtn?.addEventListener('click', () => {
        this.hideNotification(notification);
      });

      // Auto-Hide
      if (duration > 0) {
        setTimeout(() => {
          this.hideNotification(notification);
        }, duration);
      }

      return notification;
    },

    /**
     * Versteckt eine Benachrichtigung
     * @param {HTMLElement} notification - Das Notification-Element
     */
    hideNotification(notification) {
      if (!notification) return;
      notification.classList.remove('bs-configurator__notification--visible');
      setTimeout(() => {
        notification.remove();
      }, 300);
    },

    /**
     * Setzt einen Button in den Loading-Zustand
     * @param {HTMLElement} button - Der Button
     * @param {boolean} loading - Ob der Button im Loading-Zustand sein soll
     */
    setButtonLoading(button, loading) {
      if (!button) return;
      
      if (loading) {
        button.classList.add('bs-configurator__button--loading');
        button.setAttribute('disabled', 'disabled');
        // Speichere originalen Text
        if (!button.dataset.originalText) {
          button.dataset.originalText = button.textContent;
        }
      } else {
        button.classList.remove('bs-configurator__button--loading');
        // Text wird beibehalten, disabled wird separat gesteuert
      }
    }
  };

  // ============================================================================
  // DATA LOADING
  // ============================================================================
  /**
   * Lädt die Produktdaten aus dem JSON-Script-Tag
   * @returns {boolean} True wenn erfolgreich geladen
   */
  const loadProductsData = () => {
    const dataScript = document.getElementById('bs-configurator-products-data');
    
    if (!dataScript) {
      console.warn('BS Configurator: Produktdaten-Script nicht gefunden');
      return false;
    }
    
    try {
      const data = JSON.parse(dataScript.textContent);
      
      // Validiere grundlegende Struktur
      if (!data || typeof data !== 'object') {
        throw new Error('Ungültiges Datenformat');
      }
      
      if (!Array.isArray(data.products)) {
        throw new Error('Produktarray fehlt oder ungültig');
      }
      
      productsData = data;
      return true;
    } catch (error) {
      console.error('BS Configurator: Fehler beim Laden der Produktdaten:', error);
      productsData = null;
      
      // Zeige Benutzer-Feedback nur wenn Popup sichtbar ist
      const popup = document.querySelector('.bs-configurator__popup.active');
      if (popup) {
        utils.showNotification(ERROR_MESSAGES.dataLoad, 'error');
      }
      
      return false;
    }
  };

  // ============================================================================
  // BUTTON MANAGEMENT
  // ============================================================================
  const checkAllProductsSelected = () => {
    const { sizeColor, freeFlap, flap } = domCache.contentItems;
    return !!(sizeColor?.getAttribute(DATA_ATTRS.variantId) &&
              freeFlap?.getAttribute(DATA_ATTRS.variantId) &&
              flap?.getAttribute(DATA_ATTRS.variantId));
  };

  const hasBagSelection = () => !!(selectedProducts.size && selectedProducts.color);

  const updateButtonState = (button, enabled) => {
    if (!button) return;
    if (enabled) {
      button.removeAttribute('disabled');
    } else {
      button.setAttribute('disabled', 'disabled');
    }
  };

  const updateMainButton = () => {
    const allSelected = checkAllProductsSelected();
    const showCheckout = allSelected && addedToCart;

    domCache.selectionLabel?.classList.toggle('hidden', !allSelected);
    domCache.selectionTotal?.classList.toggle('hidden', !allSelected);
    domCache.selectionSuccess?.classList.toggle('hidden', !showCheckout);
    domCache.checkoutButton?.classList.toggle('hidden', !showCheckout);

    if (showCheckout) {
      if (domCache.mainButton) domCache.mainButton.classList.add('hidden');
      if (domCache.addToCartButton) domCache.addToCartButton.classList.add('hidden');
    } else if (allSelected) {
      if (domCache.mainButton) domCache.mainButton.classList.remove('hidden');
      if (domCache.addToCartButton) domCache.addToCartButton.classList.remove('hidden');
    } else {
      if (domCache.mainButton) domCache.mainButton.classList.remove('hidden');
      if (domCache.addToCartButton) domCache.addToCartButton.classList.add('hidden');
    }

    if (allSelected) updateSummaryTotal();
  };

  const clearAddedToCart = () => {
    if (!addedToCart) return;
    addedToCart = false;
    updateMainButton();
  };

  const markAddedToCart = () => {
    addedToCart = true;
    updateMainButton();
  };

  const updateSummaryButton = () => {
    updateButtonState(domCache.buttons.summaryAddToCart, checkAllProductsSelected());
  };

  // ============================================================================
  // CART OPERATIONS
  // ============================================================================
  /**
   * Fügt Items zum Warenkorb hinzu
   * @param {Array<{id: number, quantity: number}>} items - Die hinzuzufügenden Items
   * @returns {Promise<Object>} Die Cart-Response
   * @throws {Error} Bei Netzwerk- oder Server-Fehlern
   */
  const addItemsToCart = async (items) => {
    if (!items.length) {
      throw new Error('Keine Produkte zum Hinzufügen');
    }

    isLoading = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items })
      });

      // Prüfe auf spezifische Fehler
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.description || errorData.message || 'Server-Fehler';
        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Hole aktuelle Cart-Daten für cart:refresh Event
      const cartContent = await (await fetch(`${window.themeVariables.routes.cartUrl}.js`, { cache: 'reload' })).json();

      // Aktualisiere Cart mit Cart-Daten (wichtig für cart-count Update)
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { cart: cartContent }
      }));

      // Öffne Cart Drawer
      const miniCart = document.getElementById('mini-cart');
      if (miniCart) {
        miniCart.open = true;
      }

      return data;
    } catch (error) {
      console.error('BS Configurator: Fehler beim Hinzufügen zum Warenkorb:', error);
      throw error;
    } finally {
      isLoading = false;
    }
  };

  /**
   * Erstellt einen Click-Handler für den Add-to-Cart Button
   * @param {HTMLElement} button - Der Button
   * @returns {Function} Der Event-Handler
   */
  const handleAddToCart = (button) => async (e) => {
    e.preventDefault();

    if (button.hasAttribute('disabled') || isLoading) return;

    const itemsToAdd = utils.collectVariantIds();
    if (!itemsToAdd.length) {
      utils.showNotification('Bitte wähle alle Produkte aus.', 'info');
      return;
    }

    utils.setButtonLoading(button, true);

    try {
      await addItemsToCart(itemsToAdd);
      utils.showNotification('Produkte wurden zum Warenkorb hinzugefügt!', 'success', 3000);
      markAddedToCart();
    } catch (error) {
      utils.showNotification(ERROR_MESSAGES.cartAdd, 'error');
    } finally {
      utils.setButtonLoading(button, false);
      updateButtonState(button, checkAllProductsSelected());
    }
  };

  // ============================================================================
  // POPUP MANAGEMENT
  // ============================================================================
  const openConfiguratorPopup = () => {
    if (!domCache.popup) return;

    domCache.popup.classList.add('active');
    document.body.style.overflow = 'hidden';
    resetConfigurator();
  };

  const openConfiguratorAtStep = (step) => {
    if (!domCache.popup) return;

    domCache.popup.classList.add('active');
    document.body.style.overflow = 'hidden';
    switchToStep(step || 'size');
  };

  const closePopup = () => {
    if (!domCache.popup) return;

    // Tasche und Gratisklappe bleiben sichtbar, sobald Größe und Farbe gewählt sind
    if (!hasBagSelection()) {
      resetConfigurator();
    }
    domCache.popup.classList.remove('active');
    document.body.style.overflow = '';
    utils.removeHash();
  };

  const checkHashAndOpenPopup = () => {
    if (window.location.hash === '#set-creator') {
      openConfiguratorPopup();
    }
  };

  // ============================================================================
  // STEP NAVIGATION
  // ============================================================================
  /**
   * Wechselt zum angegebenen Step
   * @param {string} step - Der Ziel-Step ('size', 'color', 'flap', 'summary')
   */
  const switchToStep = (step) => {
    document.querySelectorAll('.bs-configurator__popup-content-inner.is-details-open').forEach((stepEl) => {
      stepEl.classList.remove('is-details-open');
    });

    // Setze Step-Attribut auf Popup-Content
    if (domCache.popupContent) {
      domCache.popupContent.setAttribute(DATA_ATTRS.step, step);
    }

    // Verstecke alle Steps (nutze gecachte Steps)
    domCache.steps.forEach((stepEl) => {
      stepEl.classList.add('hidden');
    });

    // Aktualisiere Kategorien-Header (nutze gecachte Kategorien)
    domCache.categories?.forEach((category) => {
      const categoryStep = category.getAttribute(DATA_ATTRS.step);
      if (categoryStep === step) {
        category.classList.remove('inactive');
        category.classList.add('active');
      } else {
        category.classList.remove('active');
        category.classList.add('inactive');
      }
    });

    // Zeige gewünschten Step (nutze gecachten Step)
    const targetStep = domCache.getStep(step);
    if (!targetStep) return;

    targetStep.classList.remove('hidden');
    restoreStepSelection(step);

    // Step-spezifische Initialisierung
    const stepHandlers = {
      color: () => {
        displaySelectedProduct();
      },
      flap: () => {
        displayFlapProductImages();
        displayFlaps();
        initFlapSelection();
        if (!selectedProducts.flap) updateSelectedFlapMeta(null);
        restoreFlapSelection();
      },
      summary: () => {
        displaySummary();
        updateSummaryTotal();
        updateSummaryButton();
        initSummaryButton();
        initPreviousButtons();
        initBackButton();
      }
    };

    stepHandlers[step]?.();
  };

  const goToPreviousStep = () => {
    const currentStepElement = document.querySelector('.bs-configurator__popup-content-inner:not(.hidden)');
    if (!currentStepElement) return;

    const currentStep = currentStepElement.getAttribute(DATA_ATTRS.step);
    const currentStepIndex = STEP_ORDER.indexOf(currentStep);

    if (currentStepIndex > 0) {
      switchToStep(STEP_ORDER[currentStepIndex - 1]);
    }
  };

  const resetToStep = (step) => {
    const popupContent = document.querySelector('.bs-configurator__popup-content');
    if (popupContent) {
      popupContent.setAttribute(DATA_ATTRS.step, step);
    }

    // Reset Auswahlen
    selectedProducts = { size: null, color: null, flap: null };

    // Entferne active Klassen
    document.querySelectorAll('.bs-configurator__popup-content-inner[data-step="size"] .bs-configurator__popup-content-inner-item').forEach((item) => {
      item.classList.remove('active');
    });

    // Deaktiviere Buttons
    document.querySelectorAll('.bs-configurator__popup-content-inner-button--next').forEach((button) => {
      button.setAttribute('disabled', 'disabled');
    });

    switchToStep(step);
  };

  // ============================================================================
  // STEP SELECTION HANDLERS
  // ============================================================================
  const initStepSize = () => {
    const sizeStep = domCache.getStep('size');
    if (!sizeStep) return;
    
    const sizeItems = sizeStep.querySelectorAll('.bs-configurator__popup-content-inner-item');
    const nextButton = domCache.getNextButton('size');

    if (!sizeItems.length) return;

    sizeItems.forEach((item) => {
      item.addEventListener('click', () => {
        clearAddedToCart();
        // Entferne active Klasse von allen Items
        sizeItems.forEach((i) => i.classList.remove('active'));
        item.classList.add('active');

        updateButtonState(nextButton, true);

        const productId = parseInt(item.getAttribute(DATA_ATTRS.productId), 10);
        const variantId = parseInt(item.getAttribute(DATA_ATTRS.variantId), 10);

        // Prüfe ob gespeicherte Farbe zu neuem Produkt passt
        if (selectedProducts.size?.productId !== productId) {
          const newProduct = utils.findProduct(productId);
          if (newProduct?.color_variants && selectedProducts.color) {
            const colorExists = newProduct.color_variants.some((cv) => cv.id === selectedProducts.color.productId);
            if (!colorExists) {
              selectedProducts.color = null;
            }
          } else {
            selectedProducts.color = null;
          }
        }

        selectedProducts.size = { productId, variantId };

        // Speichere im Content-Item
        if (domCache.contentItems.sizeColor) {
          domCache.contentItems.sizeColor.setAttribute(DATA_ATTRS.productId, productId);
          domCache.contentItems.sizeColor.setAttribute(DATA_ATTRS.variantId, variantId);
        }

        switchToStep('color');
      });
    });

    if (nextButton) {
      nextButton.addEventListener('click', () => {
        if (selectedProducts.size) {
          if (domCache.contentItems.sizeColor) {
            domCache.contentItems.sizeColor.setAttribute(DATA_ATTRS.productId, selectedProducts.size.productId);
            domCache.contentItems.sizeColor.setAttribute(DATA_ATTRS.variantId, selectedProducts.size.variantId);
          }
        }
        switchToStep('color');
      });
    }
  };

  const initColorSwatches = () => {
    const colorSwatches = document.querySelectorAll('.bs-configurator__color-swatch');
    const nextButton = domCache.getNextButton('color');

    if (!colorSwatches.length) return;

    colorSwatches.forEach((swatch) => {
      swatch.addEventListener('click', () => {
        clearAddedToCart();
        colorSwatches.forEach((s) => s.classList.remove('active'));
        swatch.classList.add('active');

        updateButtonState(nextButton, true);

        const productId = parseInt(swatch.getAttribute(DATA_ATTRS.colorVariantId), 10);
        const variantId = parseInt(swatch.getAttribute(DATA_ATTRS.colorVariantVariantId), 10);

        selectedProducts.color = { productId, variantId };

        updateColorPreviewImage(productId);
        displayFreeFlap();
        updateContentItems();
      });
    });

    if (nextButton) {
      nextButton.addEventListener('click', () => switchToStep('flap'));
    }
  };

  const initFlapSelection = () => {
    const flapItems = document.querySelectorAll('.bs-configurator__popup-content-inner-flap-product-item');
    const nextButton = domCache.getNextButton('flap');

    flapItems.forEach((flapItem) => {
      const isFreeFlap = flapItem.classList.contains('bs-configurator__popup-content-inner-flap-product-item--free-flap');
      if (isFreeFlap) return;

      flapItem.addEventListener('click', () => {
        clearAddedToCart();
        flapItems.forEach((item) => {
          if (!item.classList.contains('bs-configurator__popup-content-inner-flap-product-item--free-flap')) {
            item.classList.remove('active');
          }
        });

        flapItem.classList.add('active');

        const flapId = parseInt(flapItem.getAttribute(DATA_ATTRS.flapId), 10);
        const flapVariantId = parseInt(flapItem.getAttribute(DATA_ATTRS.flapVariantId), 10);

        selectedProducts.flap = { productId: flapId, variantId: flapVariantId };

        updateButtonState(nextButton, true);
        updateFlapImage(flapId);
        updateContentItems();
      });
    });

    if (nextButton) {
      nextButton.addEventListener('click', () => {
        updateContentItems();
        const flapContentItem = document.querySelector(`.bs-configurator__content-item${STEP_SELECTORS.flap}`);
        if (flapContentItem) {
          flapContentItem.classList.add('active');
        }
        switchToStep('summary');
      });
    }
  };

  /**
   * Stellt die vorherige Auswahl eines Steps wieder her
   * @param {string} step - Der Step-Name
   */
  const restoreStepSelection = (step) => {
    if (step === 'size' && selectedProducts.size) {
      const sizeStep = domCache.getStep('size');
      if (!sizeStep) return;
      
      const sizeItems = sizeStep.querySelectorAll('.bs-configurator__popup-content-inner-item');
      const nextButton = domCache.getNextButton('size');

      sizeItems.forEach((item) => {
        const productId = utils.parseDataAttr(item, DATA_ATTRS.productId);
        if (productId === selectedProducts.size.productId) {
          item.classList.add('active');
          updateButtonState(nextButton, true);
        }
      });
    } else if (step === 'color' && selectedProducts.color) {
      const colorSwatches = document.querySelectorAll('.bs-configurator__color-swatch');
      const nextButton = domCache.getNextButton('color');

      colorSwatches.forEach((swatch) => {
        const productId = utils.parseDataAttr(swatch, DATA_ATTRS.colorVariantId);
        if (productId === selectedProducts.color.productId) {
          swatch.classList.add('active');
          updateButtonState(nextButton, true);
        }
      });

      displayFreeFlap();
    }
  };

  // ============================================================================
  // DISPLAY FUNCTIONS
  // ============================================================================
  const getColorSwatchStyle = (colorValue, colorSwatchConfig) => {
    if (!colorValue || !colorSwatchConfig) {
      return `background-color: ${colorValue || 'transparent'}`;
    }

    const colorValueDowncase = colorValue.toLowerCase().trim();
    let style = `background-color: ${colorValueDowncase}`;

    const configLines = colorSwatchConfig.split('\n');
    for (const line of configLines) {
      const trimmedLine = line.trim();
      if (!trimmedLine) continue;

      const parts = trimmedLine.split(':');
      if (parts.length < 2) continue;

      const swatchName = parts[0].toLowerCase().trim();
      const swatchValue = parts.slice(1).join(':').trim();

      if (colorValueDowncase === swatchName) {
        if (swatchValue.startsWith('#')) {
          style = `background-color: ${swatchValue}`;
        } else if (/\.(png|jpg|jpeg)$/i.test(swatchValue)) {
          style = `background-image: url(https://cdn.shopify.com/s/files/1/2252/1373/files/${swatchValue})`;
        } else {
          style = `background-color: ${swatchValue}`;
        }
        break;
      }
    }

    return style;
  };

  const displaySelectedProduct = () => {
    if (!selectedProducts.size || !productsData) return;

    const selectedProduct = utils.findProduct(selectedProducts.size.productId);
    if (!selectedProduct) return;

    const productImageContainer = domCache.containers.productImage;
    const colorVariantsContainer = domCache.containers.colorVariants;

    // Bestimme welche Farbvariante für das Bild verwendet werden soll
    let imageColorVariant = null;
    if (selectedProduct.color_variants?.length) {
      if (selectedProducts.color) {
        imageColorVariant = utils.findColorVariant(selectedProduct, selectedProducts.color.productId);
      }
      if (!imageColorVariant) {
        imageColorVariant = selectedProduct.color_variants[0];
      }
    }

    const imageUrl = imageColorVariant?.configurator_img || selectedProduct.configurator_img || selectedProduct.featured_image;
    const imageSrcset = imageColorVariant?.configurator_img_srcset || selectedProduct.configurator_img_srcset || '';
    const alt = imageColorVariant?.configurator_product_name || imageColorVariant?.title || selectedProduct.title;

    if (productImageContainer && imageUrl) {
      productImageContainer.innerHTML = utils.createImageHTML({ src: imageUrl, srcset: imageSrcset, alt });
    }

    // Rendere Farbvarianten als Swatches
    if (colorVariantsContainer && selectedProduct.color_variants?.length) {
      let hadColorSelection = false;
      if (selectedProducts.color) {
        const colorExists = selectedProduct.color_variants.some((cv) => cv.id === selectedProducts.color.productId);
        if (!colorExists) {
          selectedProducts.color = null;
        } else {
          hadColorSelection = true;
        }
      }

      if (!selectedProducts.color && selectedProduct.color_variants.length) {
        selectedProducts.color = {
          productId: selectedProduct.color_variants[0].id,
          variantId: selectedProduct.color_variants[0].variant_id
        };
      }

      const swatchesHTML = selectedProduct.color_variants.map((colorVariant, index) => {
        const colorValue = colorVariant.color || '';
        const swatchStyle = getColorSwatchStyle(colorValue, productsData.color_swatch_config);
        const isSelected = selectedProducts.color?.productId === colorVariant.id;
        const shouldBeActive = isSelected || (!hadColorSelection && index === 0);
        const escapedTitle = utils.escapeHTML(colorVariant.title || colorValue);
        const escapedColorValue = utils.escapeHTML(colorValue);

        return `
          <div class="bs-configurator__color-swatch${shouldBeActive ? ' active' : ''}" 
               ${DATA_ATTRS.colorVariantId}="${colorVariant.id}" 
               ${DATA_ATTRS.colorVariantVariantId}="${colorVariant.variant_id}">
            <div class="bs-configurator__color-swatch__item" style="${swatchStyle}">
              <span class="visually-hidden">${escapedTitle}</span>
            </div>
            ${colorValue ? `<div class="bs-configurator__color-swatch__label">${escapedColorValue}</div>` : ''}
          </div>
        `;
      }).join('');

      colorVariantsContainer.innerHTML = `<div class="bs-configurator__color-swatch-list">${swatchesHTML}</div>`;

      initColorSwatches();
      displayFreeFlap();

      const nextButton = domCache.getNextButton('color');
      if (nextButton && selectedProducts.color) {
        updateButtonState(nextButton, true);
      }

      if (selectedProducts.color) {
        updateContentItems();
      }
    } else {
      displayFreeFlap();
    }
  };

  const displayFreeFlap = () => {
    if (!productsData || !selectedProducts.size) return;

    const selectedProduct = utils.findProduct(selectedProducts.size.productId);
    if (!selectedProduct?.color_variants?.length) return;

    let selectedColorVariant = null;
    if (selectedProducts.color) {
      selectedColorVariant = utils.findColorVariant(selectedProduct, selectedProducts.color.productId);
    }
    if (!selectedColorVariant) {
      selectedColorVariant = selectedProduct.color_variants[0];
    }
    if (!selectedColorVariant) return;

    const freeFlapContainer = domCache.containers.freeFlap;
    if (!freeFlapContainer) return;

    const freeFlap = selectedColorVariant.free_flap;
    if (!utils.isValidFreeFlap(freeFlap)) {
      freeFlapContainer.innerHTML = '';
      return;
    }

    const badgeBgColor = productsData.popup_free_flap_badge_background_color || 'rgba(0,0,0,0)';
    const badgeTextColor = productsData.popup_free_flap_badge_text_color || 'rgba(0,0,0,0)';
    const badgeText = utils.escapeHTML(productsData.popup_free_flap_badge_text || 'Gratis');
    const priceFormatted = utils.escapeHTML(freeFlap.price_formatted || utils.formatPrice(freeFlap.price));

    const tvaTextElement = freeFlapContainer.querySelector('.bs-configurator__free-flap-tva-text');
    const tvaTextHTML = tvaTextElement?.innerHTML || '';

    const productName = freeFlap.produkt_titel || freeFlap.configurator_product_name || freeFlap.title || '';
    const titleWithColor = utils.escapeHTML(utils.flapLabel(freeFlap));

    const freeFlapHTML = `
      <div class="bs-configurator__free-flap">
        <div class="bs-configurator__free-flap-image">
          ${freeFlap.featured_image ? utils.createImageHTML({ src: freeFlap.featured_image, alt: productName }) : ''}
        </div>
        <div class="bs-configurator__free-flap-content">
          <div class="bs-configurator__free-flap-header">
            <div class="bs-configurator__free-flap-title">${titleWithColor}</div>
          </div>
          <div class="bs-configurator__free-flap-price-wrapper">
            <div class="bs-configurator__free-flap-price">${priceFormatted}</div>
            <span class="bs-configurator__free-flap-gratis" style="color: ${utils.escapeAttr(badgeBgColor)};">${badgeText}</span>
          </div>
          ${tvaTextHTML}
        </div>
      </div>
    `;

    freeFlapContainer.innerHTML = freeFlapHTML;

    if (tvaTextElement && !freeFlapContainer.querySelector('.bs-configurator__free-flap-tva-text')) {
      freeFlapContainer.appendChild(tvaTextElement);
    }
  };

  /**
   * Zeigt die Produktbilder im Flap-Auswahl-Step an
   */
  const displayFlapProductImages = () => {
    const { product, colorVariant } = utils.getSelectedProductAndVariant();
    if (!product || !colorVariant) return;

    const mainImageContainer = domCache.containers.flapImageMain;
    const flapImageContainer = domCache.containers.flapImageFlap;

    if (!mainImageContainer || !flapImageContainer) return;

    const selectedColorVariant = colorVariant;

    // Bild der main bag
    const mainImageUrl = selectedColorVariant.configurator_img || selectedColorVariant.featured_image;
    const mainImageSrcset = selectedColorVariant.configurator_img_srcset || '';
    const mainAlt = selectedColorVariant.configurator_product_name || selectedColorVariant.title;

    if (mainImageUrl) {
      mainImageContainer.innerHTML = utils.createImageHTML({ 
        src: mainImageUrl, 
        srcset: mainImageSrcset, 
        alt: mainAlt,
        loading: 'eager' // Sofort laden im Popup
      });
    }

    showFreeFlapImage(selectedColorVariant);
  };

  const showFreeFlapImage = (colorVariant) => {
    const flapImageContainer = domCache.containers.flapImageFlap;
    if (!flapImageContainer || !utils.isValidFreeFlap(colorVariant?.free_flap)) return;

    const freeFlap = colorVariant.free_flap;
    const flapImageUrl = freeFlap.configurator_img || freeFlap.featured_image;
    const flapImageSrcset = freeFlap.configurator_img_srcset || '';
    const flapAlt = freeFlap.configurator_product_name || freeFlap.title;

    if (flapImageUrl) {
      flapImageContainer.innerHTML = utils.createImageHTML({ 
        src: flapImageUrl, 
        srcset: flapImageSrcset, 
        alt: flapAlt,
        loading: 'eager' // Sofort laden im Popup
      });
    }
  };

  /**
   * Zeigt die Gratisklappe wieder auf der Tasche und hebt die Auswahl der Wechselklappe auf
   */
  const previewFreeFlap = () => {
    const { colorVariant } = utils.getSelectedProductAndVariant();
    if (!utils.isValidFreeFlap(colorVariant?.free_flap)) return;

    clearAddedToCart();
    selectedProducts.flap = null;

    document.querySelectorAll('.bs-configurator__popup-content-inner-flap-product-item.active').forEach((item) => {
      item.classList.remove('active');
    });

    restoreContentItem(domCache.contentItems.flap);

    showFreeFlapImage(colorVariant);
    updateSelectedFlapMeta(null);
    updateButtonState(domCache.getNextButton('flap'), false);
    updateMainButton();
    updateSummaryButton();
  };

  /**
   * Zeigt die verfügbaren Flaps an
   */
  const displayFlaps = () => {
    const { colorVariant } = utils.getSelectedProductAndVariant();
    if (!colorVariant) return;

    const flapsContainer = domCache.containers.flapsContainer;
    if (!flapsContainer) return;

    const selectedColorVariant = colorVariant;
    let flapsHTML = '';

    if (selectedColorVariant.flaps?.length) {
      selectedColorVariant.flaps.forEach((flap) => {
        if (utils.isAvailable(flap.available)) {
          const flapAlt = flap.configurator_product_name || flap.title || '';
          flapsHTML += `
            <div class="bs-configurator__popup-content-inner-flap-product-item" 
                 ${DATA_ATTRS.flapId}="${flap.id}" 
                 ${DATA_ATTRS.flapVariantId}="${flap.variant_id}">
              <div class="bs-configurator__popup-content-inner-flap-product-item-image">
                ${flap.featured_image ? utils.createImageHTML({ src: flap.featured_image, alt: flapAlt }) : ''}
              </div>
            </div>
          `;
        }
      });
    }

    flapsContainer.innerHTML = flapsHTML;
    displayIncludedFlap();
    initFlapSelection();
  };

  const displayIncludedFlap = () => {
    const container = document.querySelector('.bs-configurator__flap-included');
    if (!container) return;

    const { colorVariant } = utils.getSelectedProductAndVariant();
    const freeFlap = colorVariant?.free_flap;
    if (!utils.isValidFreeFlap(freeFlap)) {
      container.innerHTML = '';
      return;
    }

    const label = container.getAttribute('data-label') || 'Bereits gratis dabei:';
    const name = freeFlap.produkt_titel || freeFlap.configurator_product_name || freeFlap.title || '';
    const title = utils.flapLabel(freeFlap);
    const imageSrc = freeFlap.configurator_thumbnail || freeFlap.featured_image;

    container.innerHTML = `
      <div class="bs-configurator__flap-included-image">
        ${imageSrc ? utils.createImageHTML({ src: imageSrc, alt: name }) : ''}
      </div>
      <div class="bs-configurator__flap-included-text">
        <div class="bs-configurator__flap-included-label">${utils.escapeHTML(label)}</div>
        <div class="bs-configurator__flap-included-name">${utils.escapeHTML(title)}</div>
      </div>
    `;

    container.setAttribute('role', 'button');
    container.setAttribute('tabindex', '0');
    utils.addEventListenerOnce(container, 'click', previewFreeFlap);
    if (!container.hasAttribute('data-keydown-added')) {
      container.setAttribute('data-keydown-added', 'true');
      container.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          previewFreeFlap();
        }
      });
    }
  };

  const updateSelectedFlapMeta = (flap) => {
    const titleElement = document.querySelector('.bs-configurator__flap-selected-title');
    const priceElement = document.querySelector('.bs-configurator__flap-selected-price');
    if (!titleElement || !priceElement) return;

    if (!flap) {
      titleElement.textContent = '';
      priceElement.innerHTML = '';
      return;
    }

    titleElement.textContent = utils.flapLabel(flap);

    const priceFormatted = flap.price_formatted || utils.formatPrice(flap.price);
    const compareFormatted = flap.compare_at_price > flap.price ? flap.compare_at_price_formatted : '';
    priceElement.innerHTML = utils.priceMarkup(priceFormatted, compareFormatted);
  };

  /**
   * Aktualisiert das Flap-Bild im Preview
   * @param {number} flapId - Die ID der ausgewählten Flap
   */
  const updateFlapImage = (flapId) => {
    const { colorVariant } = utils.getSelectedProductAndVariant();
    if (!colorVariant) return;

    const selectedFlap = utils.findFlap(colorVariant, flapId);
    if (!selectedFlap) return;

    const flapImageContainer = domCache.containers.flapImageFlap;
    if (!flapImageContainer) return;

    const imageUrl = selectedFlap.configurator_img || selectedFlap.featured_image;
    const imageSrcset = selectedFlap.configurator_img_srcset || '';
    const alt = selectedFlap.configurator_product_name || selectedFlap.title;

    if (imageUrl) {
      flapImageContainer.innerHTML = utils.createImageHTML({ 
        src: imageUrl, 
        srcset: imageSrcset, 
        alt,
        loading: 'eager' // Sofort laden im Popup
      });
    }

    updateSelectedFlapMeta(selectedFlap);
  };

  const restoreFlapSelection = () => {
    if (!selectedProducts.flap) return;

    const flapItems = document.querySelectorAll('.bs-configurator__popup-content-inner-flap-product-item');
    const nextButton = document.querySelector('.bs-configurator__popup-content-inner[data-step="flap"] .bs-configurator__popup-content-inner-button--next');

    flapItems.forEach((flapItem) => {
      if (flapItem.classList.contains('bs-configurator__popup-content-inner-flap-product-item--free-flap')) return;

      const flapId = parseInt(flapItem.getAttribute(DATA_ATTRS.flapId), 10);
      if (flapId === selectedProducts.flap.productId) {
        flapItem.classList.add('active');
        updateFlapImage(flapId);
        updateButtonState(nextButton, true);
      }
    });
  };

  const updateColorPreviewImage = (colorVariantId) => {
    if (!productsData || !selectedProducts.size) return;

    const selectedProduct = utils.findProduct(selectedProducts.size.productId);
    if (!selectedProduct?.color_variants) return;

    const selectedColorVariant = selectedProduct.color_variants.find((cv) => cv.id === colorVariantId);
    if (!selectedColorVariant) return;

    const productImageContainer = document.querySelector('.bs-configurator__popup-content-inner-color-product-image');
    if (!productImageContainer) return;

    const imageUrl = selectedColorVariant.configurator_img || selectedColorVariant.featured_image || '';
    const imageSrcset = selectedColorVariant.configurator_img_srcset || '';

    if (imageUrl) {
      productImageContainer.innerHTML = utils.createImageHTML({ 
        src: imageUrl, 
        srcset: imageSrcset, 
        alt: selectedColorVariant.title 
      });
    }
  };

  // ============================================================================
  // CONTENT ITEM UPDATES
  // ============================================================================
  const updateContentItem = (item, { productId, variantId, productName, imageUrl, priceFormatted, comparePriceFormatted, hideIcon = true, isFreeFlap = false }) => {
    if (!item) return;

    const contentDiv = item.querySelector('.bs-configurator__content-item-content .bs-configurator__content-item-content');
    if (contentDiv && productName) {
      contentDiv.textContent = productName;
    }

    const imageContainer = item.querySelector('.bs-configurator__content-item-image');
    if (imageContainer) {
      const step = item.getAttribute(DATA_ATTRS.step);
      
      // Entferne nur dynamisch hinzugefügte Bilder
      const dynamicImages = imageContainer.querySelectorAll(`img[${DATA_ATTRS.dynamicImage}="true"]`);
      dynamicImages.forEach((img) => img.remove());
      
      if (imageUrl) {
        // Entferne initiale Bilder wenn ein Produktbild hinzugefügt wird
        const initialImages = imageContainer.querySelectorAll('img:not([data-dynamic-image])');
        initialImages.forEach((img) => img.remove());
        
        // Füge neues Bild hinzu
        const newImage = document.createElement('img');
        newImage.src = imageUrl;
        newImage.alt = productName || '';
        newImage.loading = 'lazy';
        newImage.setAttribute(DATA_ATTRS.dynamicImage, 'true');
        imageContainer.appendChild(newImage);
      } else if (step && domCache.initialImages.has(step)) {
        // Stelle initiale Bilder wieder her, wenn kein neues Bild vorhanden ist
        const hasInitialImages = Array.from(imageContainer.children).some(
          (child) => !child.hasAttribute(DATA_ATTRS.dynamicImage)
        );
        
        if (!hasInitialImages) {
          imageContainer.innerHTML = domCache.initialImages.get(step);
        }
      }
    }

    const priceDiv = item.querySelector('.bs-configurator__content-item-price');
    if (priceDiv) {
      if (priceFormatted) {
        // Free-Flap: Preis durchgestrichen + Badge-Text
        if (isFreeFlap && productsData) {
          const badgeText = utils.escapeHTML(productsData.popup_free_flap_badge_text || 'Gratis');
          const badgeColor = utils.escapeAttr(productsData.popup_free_flap_badge_background_color || '#EA5A0B');
          
          priceDiv.innerHTML = `
            <span class="bs-configurator__content-item-price-strikethrough">${utils.escapeHTML(priceFormatted)}</span>
            <span class="bs-configurator__content-item-price-badge" style="color: ${badgeColor};">${badgeText}</span>
          `;
          priceDiv.classList.add('bs-configurator__content-item-price--free-flap');
        } else {
          priceDiv.innerHTML = utils.priceMarkup(priceFormatted, comparePriceFormatted);
          priceDiv.classList.remove('bs-configurator__content-item-price--free-flap');
        }
        priceDiv.classList.remove('hidden');
      } else {
        priceDiv.classList.add('hidden');
        priceDiv.classList.remove('bs-configurator__content-item-price--free-flap');
        priceDiv.textContent = '';
      }
    }

    const iconDiv = item.querySelector('.bs-configurator__content-item-icon');
    if (iconDiv) {
      // Verstecke Icon wenn ein Produktbild vorhanden ist (dynamisch oder initial) oder hideIcon true ist
      const hasDynamicImage = imageContainer?.querySelector(`img[${DATA_ATTRS.dynamicImage}="true"]`);
      const hasInitialImage = imageContainer?.querySelector('img:not([data-dynamic-image])');
      const hasProductImage = imageUrl || hasDynamicImage || hasInitialImage;
      
      if (hideIcon || hasProductImage) {
        iconDiv.classList.add('hidden');
      } else {
        iconDiv.classList.remove('hidden');
      }
    }

    if (productId) item.setAttribute(DATA_ATTRS.productId, productId);
    if (variantId) item.setAttribute(DATA_ATTRS.variantId, variantId);
  };

  const updateContentItems = () => {
    if (!productsData || !selectedProducts.size || !selectedProducts.color) return;

    if (selectedProducts.flap) {
      updateFlapContentItem();
    }

    const selectedProduct = utils.findProduct(selectedProducts.size.productId);
    if (!selectedProduct) return;

    const selectedColorVariant = utils.findColorVariant(selectedProduct, selectedProducts.color.productId);
    if (!selectedColorVariant) return;

    // Update size-color item
    if (domCache.contentItems.sizeColor) {
      const productName = selectedColorVariant.configurator_product_name || selectedColorVariant.title;
      updateContentItem(domCache.contentItems.sizeColor, {
        productId: selectedColorVariant.id,
        variantId: selectedColorVariant.variant_id,
        productName,
        imageUrl: selectedColorVariant.featured_image,
        priceFormatted: selectedColorVariant.price_formatted || utils.formatPrice(selectedColorVariant.price),
        comparePriceFormatted: selectedColorVariant.compare_at_price > selectedColorVariant.price ? selectedColorVariant.compare_at_price_formatted : ''
      });
    }

    updateFreeFlapContentItem(selectedColorVariant);

    updateMainButton();
    updateSummaryButton();
  };

  const restoreContentItem = (item) => {
    if (!item) return;

    const initialState = domCache.initialContentItems.get(item);
    if (initialState) {
      item.className = initialState.className;
      item.innerHTML = initialState.innerHTML;
    }

    item.removeAttribute(DATA_ATTRS.productId);
    item.removeAttribute(DATA_ATTRS.variantId);
  };

  const updateFreeFlapContentItem = (selectedColorVariant) => {
    if (!domCache.contentItems.freeFlap) return;

    if (!utils.isValidFreeFlap(selectedColorVariant?.free_flap)) {
      restoreContentItem(domCache.contentItems.freeFlap);
      return;
    }

    const freeFlap = selectedColorVariant.free_flap;
    updateContentItem(domCache.contentItems.freeFlap, {
      productId: freeFlap.id,
      variantId: freeFlap.variant_id,
      productName: utils.flapLabel(freeFlap),
      imageUrl: freeFlap.featured_image,
      priceFormatted: freeFlap.price_formatted || utils.formatPrice(freeFlap.price),
      isFreeFlap: true
    });

    domCache.contentItems.freeFlap.classList.add('active');
  };

  const updateFlapContentItem = () => {
    if (!productsData || !selectedProducts.size || !selectedProducts.color || !selectedProducts.flap) return;

    const selectedProduct = utils.findProduct(selectedProducts.size.productId);
    if (!selectedProduct) return;

    const selectedColorVariant = utils.findColorVariant(selectedProduct, selectedProducts.color.productId);
    if (!selectedColorVariant) return;

    const selectedFlap = utils.findFlap(selectedColorVariant, selectedProducts.flap.productId);
    if (!selectedFlap) return;

    // Update flap item
    if (domCache.contentItems.flap) {
      updateContentItem(domCache.contentItems.flap, {
        productId: selectedFlap.id,
        variantId: selectedFlap.variant_id,
        productName: utils.flapLabel(selectedFlap),
        imageUrl: selectedFlap.featured_image,
        priceFormatted: utils.formatPrice(selectedFlap.price, selectedFlap.price_formatted),
        comparePriceFormatted: selectedFlap.compare_at_price > selectedFlap.price ? selectedFlap.compare_at_price_formatted : ''
      });
    }

    updateFreeFlapContentItem(selectedColorVariant);

    updateMainButton();
    updateSummaryButton();
  };

  // ============================================================================
  // SUMMARY
  // ============================================================================
  /**
   * Generiert und zeigt die Summary-Ansicht mit allen ausgewählten Produkten
   */
  const displaySummary = () => {
    const summaryContainer = domCache.containers.summaryItems;
    if (!summaryContainer) return;

    summaryContainer.innerHTML = '';

    const contentItems = document.querySelectorAll(`
      .bs-configurator__content-item${STEP_SELECTORS.sizeColor}, 
      .bs-configurator__content-item${STEP_SELECTORS.freeFlap}, 
      .bs-configurator__content-item${STEP_SELECTORS.flap}
    `);

    if (!contentItems.length) return;

    contentItems.forEach((item) => {
      const clonedItem = item.cloneNode(true);
      clonedItem.classList.remove('active');
      clonedItem.classList.add('bs-configurator__summary-item');

      // Verstecke Icon und entferne initiale Bilder wenn ein Produktbild vorhanden ist
      const imageContainer = clonedItem.querySelector('.bs-configurator__content-item-image');
      const iconDiv = clonedItem.querySelector('.bs-configurator__content-item-icon');
      
      if (iconDiv && imageContainer) {
        const hasDynamicImage = imageContainer.querySelector(`img[${DATA_ATTRS.dynamicImage}="true"]`);
        const hasInitialImage = imageContainer.querySelector('img:not([data-dynamic-image])');
        
        if (hasDynamicImage) {
          // Wenn dynamisches Bild vorhanden, entferne initiale Bilder und verstecke Icon
          const initialImages = imageContainer.querySelectorAll('img:not([data-dynamic-image])');
          initialImages.forEach((img) => img.remove());
          iconDiv.classList.add('hidden');
        } else if (hasInitialImage) {
          // Wenn nur initiale Bilder vorhanden, zeige Icon
          iconDiv.classList.remove('hidden');
        } else {
          // Wenn keine Bilder vorhanden, zeige Icon
          iconDiv.classList.remove('hidden');
        }
      }

      const wrapper = document.createElement('div');
      wrapper.className = 'bs-configurator__summary-item-wrapper';
      wrapper.appendChild(clonedItem);
      clonedItem.addEventListener('click', () => {
        switchToStep(ROW_TO_POPUP_STEP[item.getAttribute(DATA_ATTRS.step)] || 'size');
      });
      summaryContainer.appendChild(wrapper);
    });
  };

  /**
   * Aktualisiert die Gesamtpreis-Anzeige in der Summary
   */
  const updateSummaryTotal = () => {
    const { colorVariant } = utils.getSelectedProductAndVariant();
    if (!colorVariant) return;

    const selectedColorVariant = colorVariant;

    let totalPrice = 0;
    let freeFlapPrice = 0;

    // Size-Color Item
    if (selectedColorVariant.price) {
      totalPrice += selectedColorVariant.price;
    }

    // Free-Flap Item
    if (utils.isValidFreeFlap(selectedColorVariant.free_flap)) {
      const freeFlap = selectedColorVariant.free_flap;
      if (freeFlap.price) {
        freeFlapPrice = freeFlap.price;
        totalPrice += freeFlap.price;
      }
    }

    // Flap Item
    if (selectedProducts.flap) {
      const selectedFlap = utils.findFlap(selectedColorVariant, selectedProducts.flap.productId);
      if (selectedFlap?.price) {
        totalPrice += selectedFlap.price;
      }
    }

    const finalCompareAtPrice = totalPrice;
    const finalPrice = totalPrice - freeFlapPrice;

    const finalPriceFormatted = utils.formatPrice(finalPrice);
    const finalCompareAtPriceFormatted = utils.formatPrice(finalCompareAtPrice);

    const currentPriceElement = domCache.containers.summaryPriceCurrent;
    const comparePriceElement = domCache.containers.summaryPriceCompare;

    if (currentPriceElement) {
      currentPriceElement.textContent = finalPriceFormatted;
    }
    if (domCache.selectionCurrent) {
      domCache.selectionCurrent.textContent = finalPriceFormatted;
    }

    if (comparePriceElement) {
      if (finalCompareAtPrice > finalPrice) {
        comparePriceElement.textContent = finalCompareAtPriceFormatted;
        comparePriceElement.classList.remove('hidden');
      } else {
        comparePriceElement.classList.add('hidden');
      }
    }
    if (domCache.selectionCompare) {
      if (finalCompareAtPrice > finalPrice) {
        domCache.selectionCompare.textContent = finalCompareAtPriceFormatted;
        domCache.selectionCompare.classList.remove('hidden');
      } else {
        domCache.selectionCompare.classList.add('hidden');
      }
    }
  };

  // ============================================================================
  // RESET FUNCTIONS
  // ============================================================================
  const resetConfigurator = () => {
    selectedProducts = { size: null, color: null, flap: null };

    // Entferne active Klassen
    document.querySelectorAll('.bs-configurator__popup-content-inner[data-step="size"] .bs-configurator__popup-content-inner-item').forEach((item) => {
      item.classList.remove('active');
    });

    document.querySelectorAll('.bs-configurator__color-swatch').forEach((swatch) => {
      swatch.classList.remove('active');
    });

    document.querySelectorAll('.bs-configurator__popup-content-inner-flap-product-item').forEach((item) => {
      item.classList.remove('active');
    });

    // Stelle die Auswahlzeilen exakt so wieder her, wie sie initial gerendert wurden
    domCache.allContentItems?.forEach((item) => {
      const initialState = domCache.initialContentItems.get(item);
      if (initialState) {
        item.className = initialState.className;
        item.innerHTML = initialState.innerHTML;
      }

      item.removeAttribute(DATA_ATTRS.productId);
      item.removeAttribute(DATA_ATTRS.variantId);
    });

    // Deaktiviere Buttons
    document.querySelectorAll('.bs-configurator__popup-content-inner-button--next').forEach((button) => {
      button.setAttribute('disabled', 'disabled');
    });

    const summaryButton = document.querySelector('.bs-configurator__popup-content-inner[data-step="summary"] .bs-configurator__popup-content-inner-button--add-to-cart');
    if (summaryButton) {
      summaryButton.setAttribute('disabled', 'disabled');
    }

    addedToCart = false;
    updateMainButton();

    if (domCache.popup?.classList.contains('active')) {
      resetToStep('size');
    }
  };

  // ============================================================================
  // INITIALIZATION
  // ============================================================================
  const initCategoryButtons = () => {
    domCache.categories?.forEach((button) => {
      button.addEventListener('click', () => {
        const step = button.getAttribute(DATA_ATTRS.step);
        if (step) {
          switchToStep(step);
        }
      });
    });
  };

  const initPreviousButtons = () => {
    domCache.buttons.previousButtons?.forEach((button) => {
      utils.addEventListenerOnce(button, 'click', goToPreviousStep);
    });
  };

  const initBackButton = () => {
    domCache.buttons.backButtons?.forEach((backButton) => {
      utils.addEventListenerOnce(backButton, 'click', goToPreviousStep);
    });
  };

  /**
   * Initialisiert den Add-to-Cart Button in der Summary
   */
  const initSummaryButton = () => {
    const addToCartButton = domCache.buttons.summaryAddToCart;
    if (!addToCartButton) return;

    utils.addEventListenerOnce(addToCartButton, 'click', async (e) => {
      e.preventDefault();

      if (addToCartButton.hasAttribute('disabled') || isLoading) return;

      const itemsToAdd = utils.collectVariantIds();
      if (!itemsToAdd.length) {
        utils.showNotification('Bitte wähle alle Produkte aus.', 'info');
        return;
      }

      utils.setButtonLoading(addToCartButton, true);

      try {
        await addItemsToCart(itemsToAdd);
        utils.showNotification('Produkte wurden zum Warenkorb hinzugefügt!', 'success', 3000);
        closePopup();
        markAddedToCart();
      } catch (error) {
        utils.showNotification(ERROR_MESSAGES.cartAdd, 'error');
      } finally {
        utils.setButtonLoading(addToCartButton, false);
        updateButtonState(addToCartButton, checkAllProductsSelected());
      }
    });
  };

  const initMainButton = () => {
    // Standard-Button (Link zum Popup) - Reset beim Klick
    if (domCache.mainButton) {
      domCache.mainButton.addEventListener('click', () => {
        resetConfigurator();
      });
    }

    // Add-to-Cart Button
    if (domCache.addToCartButton) {
      domCache.addToCartButton.addEventListener('click', async (e) => {
        e.preventDefault();
        
        if (isLoading) return;

        const itemsToAdd = utils.collectVariantIds();
        if (!itemsToAdd.length) {
          utils.showNotification('Bitte wähle alle Produkte aus.', 'info');
          return;
        }

        utils.setButtonLoading(domCache.addToCartButton, true);

        try {
          await addItemsToCart(itemsToAdd);
          utils.showNotification('Produkte wurden zum Warenkorb hinzugefügt!', 'success', 3000);
          markAddedToCart();
        } catch (error) {
          utils.showNotification(ERROR_MESSAGES.cartAdd, 'error');
        } finally {
          utils.setButtonLoading(domCache.addToCartButton, false);
        }
      });
    }
  };

  const closeDetails = () => {
    document.querySelectorAll('.bs-configurator__popup-content-inner.is-details-open').forEach((stepEl) => {
      stepEl.classList.remove('is-details-open');
    });
  };

  const openDetails = () => {
    const { colorVariant } = utils.getSelectedProductAndVariant();
    const step = document.querySelector('.bs-configurator__popup-content-inner:not(.hidden)');
    if (!step) return;

    let description = colorVariant?.description;
    if (step.getAttribute(DATA_ATTRS.step) === 'flap') {
      const flap = selectedProducts.flap
        ? utils.findFlap(colorVariant, selectedProducts.flap.productId)
        : colorVariant?.free_flap;
      description = flap?.description;
    }

    const body = step.querySelector('.bs-configurator__details-body');
    if (body) {
      body.innerHTML = description || '<p>Keine Produktdetails verfügbar.</p>';
    }
    step.scrollTop = 0;
    step.classList.add('is-details-open');
  };

  const initConfiguratorPopup = () => {
    if (!domCache.popup) return;

    if (domCache.mediaTrigger) {
      domCache.mediaTrigger.addEventListener('click', (e) => {
        e.preventDefault();
        openConfiguratorPopup();
      });
    }

    if (!domCache.allContentItems?.length) return;

    // Event Listener für Content Items (nutze gecachte Items)
    domCache.allContentItems.forEach((item) => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (hasBagSelection()) {
          openConfiguratorAtStep(ROW_TO_POPUP_STEP[item.getAttribute(DATA_ATTRS.step)] || 'size');
          return;
        }
        openConfiguratorPopup();
      });
    });

    // Schließen-Button
    const closeBtn = domCache.popup.querySelector('.bs-configurator__popup-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        closePopup();
      });
    }

    // Overlay
    const overlay = domCache.popup.querySelector('.bs-configurator__popup-overlay');
    if (overlay) {
      overlay.addEventListener('click', closePopup);
    }

    // Popup Content (verhindere Schließen bei Klick auf Content)
    if (domCache.popupContent) {
      domCache.popupContent.addEventListener('click', (e) => {
        e.stopPropagation();
        if (e.target.closest('.bs-configurator__details-button')) {
          openDetails();
        }
        if (e.target.closest('.bs-configurator__details-back')) {
          closeDetails();
        }
      });
    }

    // Klick direkt auf Popup
    domCache.popup.addEventListener('click', (e) => {
      if (e.target === domCache.popup) {
        closePopup();
      }
    });

    // ESC-Taste
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && domCache.popup.classList.contains('active')) {
        closePopup();
      }
    });

    initStepSize();
    initCategoryButtons();
    initPreviousButtons();
    initBackButton();
  };

  // ============================================================================
  // DOM CONTENT LOADED
  // ============================================================================
  document.addEventListener('DOMContentLoaded', () => {
    loadProductsData();
    domCache._init();
    initConfiguratorPopup();
    initMainButton();
    updateMainButton();
    checkHashAndOpenPopup();

    window.addEventListener('hashchange', checkHashAndOpenPopup);
  });
})();
